<template>
  <div v-if="shouldShowRail" class="account-rail">
    <!-- Account list -->
    <div class="accounts-container">
      <div
        v-for="account in sortedAccounts"
        :key="account.email"
        class="account-item"
        :class="{ 
          active: account.email === activeEmail,
          switching: isSwitching && switchingTo === account.email
        }"
        :data-tooltip="account.companyName"
        @click="handleAccountClick(account)"
        @contextmenu="handleContextMenu($event, account)"
      >
        <div
          class="account-avatar"
          :style="{ backgroundColor: account.color }"
        >
          <span class="account-initials">{{ account.initials }}</span>
        </div>
        <div class="active-indicator"></div>
        <div v-if="isSwitching && switchingTo === account.email" class="loading-spinner">
          <i class="el-icon-loading"></i>
        </div>
      </div>
    </div>

    <!-- Context menu -->
    <div
      v-if="contextMenu.visible"
      class="context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
    >
      <div class="context-menu-item logout" @click="handleRemoveAccount">
        <i class="el-icon-switch-button"></i>
        <span>Log Out</span>
      </div>
    </div>

    <!-- Divider -->
    <div class="rail-divider"></div>

    <!-- Add account button -->
    <div 
      class="add-account-btn" 
      data-tooltip="Add Account"
      @click="showAddAccountModal = true"
    >
      <i class="el-icon-plus"></i>
    </div>
    
    <!-- Add Account Modal -->
    <add-account-modal
      :visible="showAddAccountModal"
      @close="showAddAccountModal = false"
      @account-added="handleAccountAdded"
    />
  </div>
</template>

<script>
import AddAccountModal from './AddAccountModal.vue';

export default {
  name: 'AccountRail',
  
  components: {
    AddAccountModal,
  },
  
  data() {
    return {
      switchingTo: null,
      showAddAccountModal: false,
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        account: null,
      },
    };
  },
  
  computed: {
    shouldShowRail() {
      return this.$store.getters['accountSwitcher/shouldShowRail'];
    },
    
    sortedAccounts() {
      return this.$store.getters['accountSwitcher/sortedAccounts'];
    },
    
    activeEmail() {
      return this.$store.state.accountSwitcher.activeEmail;
    },
    
    isSwitching() {
      return this.$store.state.accountSwitcher.isSwitching;
    },
  },
  
  mounted() {
    this._closeContextMenu = () => this.closeContextMenu();
    document.addEventListener('click', this._closeContextMenu);
  },

  beforeDestroy() {
    document.removeEventListener('click', this._closeContextMenu);
  },

  methods: {
    async handleAccountClick(account) {
      if (account.email === this.activeEmail) {
        return; // Already active
      }

      if (this.isSwitching) {
        return; // Already switching
      }

      this.switchingTo = account.email;

      try {
        await this.$store.dispatch('accountSwitcher/switchAccount', account.email);
      } catch (error) {
        this.$message({
          type: 'error',
          message: error.message || 'Failed to switch account',
          duration: 3000,
        });
        this.switchingTo = null;
      }
    },

    handleAccountAdded() {
      // Account was added successfully - modal handles the page reload
    },

    handleContextMenu(event, account) {
      event.preventDefault();
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        account,
      };
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
      this.contextMenu.account = null;
    },

    async handleRemoveAccount() {
      const account = this.contextMenu.account;
      if (!account) return;

      this.closeContextMenu();

      try {
        await this.$confirm(
          `Log out of "${account.companyName}"?`,
          'Log Out',
          {
            confirmButtonText: 'Log Out',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        );
      } catch {
        return; // User cancelled
      }

      const isActive = account.email === this.activeEmail;
      const accounts = this.$store.getters['accountSwitcher/sortedAccounts'];
      const hasOtherAccounts = accounts.length > 1;

      if (isActive && !hasOtherAccounts) {
        // Last account - full logout
        await this.$store.dispatch('accountSwitcher/removeAccount', account.email);
        await this.$store.dispatch('authen/logout');
        return;
      }

      if (isActive && hasOtherAccounts) {
        // Active account with others remaining - remove then switch to most recently accessed
        const otherAccounts = accounts
          .filter(a => a.email !== account.email)
          .sort((a, b) => b.lastAccess - a.lastAccess);
        const switchTo = otherAccounts[0];

        await this.$store.dispatch('accountSwitcher/removeAccount', account.email);
        await this.$store.dispatch('accountSwitcher/switchAccount', switchTo.email);
        return;
      }

      // Non-active account - just remove
      await this.$store.dispatch('accountSwitcher/removeAccount', account.email);
    },
  },
};
</script>

<style lang="scss" scoped>
.account-rail {
  width: 48px;
  min-width: 48px;
  height: 100vh;
  background: linear-gradient(180deg, #4a1e8f 0%, #3d1875 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0 24px;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.accounts-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0;
  width: 100%;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 0;
  }
}

.account-item {
  position: relative;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: 10px;
  flex-shrink: 0;
  
  /* Tooltip logic */
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 48px;
    top: 50%;
    transform: translateY(-50%) translateX(-8px);
    background: #111827;
    color: white;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 200;
    pointer-events: none;
  }
  
  &::before {
    /* Tooltip arrow */
    content: '';
    position: absolute;
    left: 44px;
    top: 50%;
    transform: translateY(-50%) translateX(-8px);
    border-width: 5px;
    border-style: solid;
    border-color: transparent #111827 transparent transparent;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 200;
    pointer-events: none;
  }
  
  &:hover {
    &::after, &::before {
      opacity: 1;
      visibility: visible;
      transform: translateY(-50%) translateX(0);
    }
    
    .account-avatar {
      border-radius: 10px; /* Squircle on hover */
    }
  }
  
  &.active {
    .account-avatar {
      border-radius: 10px;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.95);
    }
    
    .active-indicator {
      height: 20px;
      opacity: 1;
    }
  }
  
  &.switching {
    pointer-events: none;
    opacity: 0.7;
    
    .account-avatar {
      filter: blur(1px);
    }
  }
}

.account-avatar {
  width: 36px;
  height: 36px;
  border-radius: 14px; /* Slightly more rounded default */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  background-size: cover;
  background-position: center;
  border: 2px solid transparent; /* Reserve space for border to prevent layout shift */
  box-sizing: border-box;
}

.account-initials {
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  user-select: none;
}

.active-indicator {
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 0;
  opacity: 0;
  background: #ffffff;
  border-radius: 0 3px 3px 0;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.loading-spinner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 10px;
  
  i {
    color: #ffffff;
    font-size: 16px;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.rail-divider {
  width: 20px;
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 1px;
  margin: 8px 0;
  flex-shrink: 0;
}

.add-account-btn {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 14px; /* Match avatar style */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.08);
  border: 1px dashed rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
  
  /* Tooltip logic */
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 48px;
    top: 50%;
    transform: translateY(-50%) translateX(-8px);
    background: #111827;
    color: white;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 200;
    pointer-events: none;
  }
  
  &::before {
    /* Tooltip arrow */
    content: '';
    position: absolute;
    left: 44px;
    top: 50%;
    transform: translateY(-50%) translateX(-8px);
    border-width: 5px;
    border-style: solid;
    border-color: transparent #111827 transparent transparent;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 200;
    pointer-events: none;
  }
  
  i {
    color: rgba(255, 255, 255, 0.6);
    font-size: 16px;
    transition: all 0.2s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.8);
    border-style: solid;
    border-radius: 10px;
    
    &::after, &::before {
      opacity: 1;
      visibility: visible;
      transform: translateY(-50%) translateX(0);
    }
    
    i {
      color: #ffffff;
    }
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.context-menu {
  position: fixed;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1);
  padding: 4px 0;
  z-index: 9999;
  min-width: 160px;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s;

  &.logout {
    color: #CC3030;

    &:hover {
      background: #FFF1F0;
    }
  }

  i {
    font-size: 14px;
  }
}

/* Responsive - hide on very small screens */
@media only screen and (max-width: 768px) {
  .account-rail {
    display: none;
  }
}
</style>
